import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { Card, CardHeader, Box, IconButton } from '@mui/material';
import ExpandContract from '@mui/icons-material/OpenInFull';
import { useChart } from '../../../components/chart';

AppChartCard.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  chartData: PropTypes.array.isRequired,
  expandContractChart: PropTypes.func,
  doubleYAxis: PropTypes.bool,
  xAxisLabel: PropTypes.string,
  forceYMinZero: PropTypes.bool,
  xMinMs: PropTypes.number,
  xMaxMs: PropTypes.number,
  yAxisMin: PropTypes.number,
  yAxisMax: PropTypes.number,
};

export default function AppChartCard({
  title,
  subheader,
  chartData,
  expandContractChart,
  doubleYAxis,
  xAxisLabel,
  forceYMinZero,
  xMinMs,
  xMaxMs,
  yAxisMin,
  yAxisMax,
  ...other
}) {
  const yaxisvariant = doubleYAxis
    ? [
        {},
        {
          opposite: true,
          show: false,
          axisTicks: { show: false },
          axisBorder: { show: false },
          labels: { show: false },
          tickAmount: 0,
        },
      ]
    : [{}];


  let derivedMin = Number.POSITIVE_INFINITY;
  let derivedMax = Number.NEGATIVE_INFINITY;
  (chartData ?? []).forEach((s) => {
    (s?.data ?? []).forEach((pt) => {
      if (typeof pt?.x === 'number') {
        if (pt.x < derivedMin) derivedMin = pt.x;
        if (pt.x > derivedMax) derivedMax = pt.x;
      }
    });
  });
  const fallbackMin = Number.isFinite(derivedMin) ? derivedMin : Date.now() - 24 * 3600 * 1000;
  const fallbackMax = Number.isFinite(derivedMax) ? derivedMax : Date.now();

  const hasBar = chartData?.some((s) => s.type === 'bar');

  const chartOptions = useChart({
    chart: { stacked: false },
    plotOptions: { bar: { columnWidth: '90%' } },
    fill: {
      type: chartData.map((s) => s.type === 'bar' ? 'solid' : s.type === 'area' ? 'gradient' : 'solid'),
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 90, 100],
        colorStops: [
          { offset: 0, color: '#06284B', opacity: 0.6 },
          { offset: 100, color: '#06284B', opacity: 0.1 },
        ],
      },
    },

    xaxis: {
      type: 'datetime',
      min: typeof xMinMs === 'number' ? xMinMs : fallbackMin,
      max: typeof xMaxMs === 'number' ? xMaxMs : fallbackMax,
      ...(hasBar ? { tickPlacement: 'on' } : {}),
      labels: {
        datetimeUTC: false,
        format: 'MM/dd HH:mm',
        hideOverlappingLabels: true,
        showDuplicates: false,
        offsetX: 5,
      },
      title: {
        text: xAxisLabel,
        offsetY: 3,
        offsetX: -20,
        style: {
          fontSize: '12px',
          fontWeight: 100,
          cssClass: 'apexcharts-xaxis-title',
        },
      },
    },
    yaxis: yaxisvariant.map((axis) => ({
      ...axis,
      ...(typeof yAxisMin === 'number'
        ? { min: yAxisMin }
        : forceYMinZero
        ? { min: 0 }
        : {}),
      ...(typeof yAxisMax === 'number' ? { max: yAxisMax } : {}),
      labels: {
        ...(axis.labels || {}),
        formatter: (val) => (typeof val === 'number' ? Math.round(val).toFixed(2) : val),
      },
    })),
    tooltip: {
      shared: true,
      intersect: false,
      style: { fontSize: '10px' },
      x: { format: 'MM/dd HH:mm' },
      y: { formatter: (y) => (typeof y !== 'undefined' ? `${y}` : y) },
    },
    legend: { labels: { style: { fontSize: '8px' } } },
  });

  return (
    <Card {...other}>
      <CardHeader
        title={title}
        subheader={subheader}
        sx={{ py: 1.5, px: 1.75 }}
        subheaderTypographyProps={{ sx: { fontSize: '12px' } }}
        action={
          <IconButton size="small" aria-label="expand" onClick={() => expandContractChart?.()}>
            <ExpandContract />
          </IconButton>
        }
      />
      <Box sx={{ pt: 0.5, px: 1.5, pb: 1 }} dir="ltr">
        <ReactApexChart type="line" series={chartData} options={chartOptions} height={250} />
      </Box>
    </Card>
  );
}
